import type { AnimationEvent, ReactNode } from "react";
import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Checkbox, Tooltip } from "@similarweb/ui-components";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { StyledTextOverflow } from "app/pages/sales-intelligence/common-components/OverflowTooltip/styles";
import { useIsTextTruncated } from "app/pages/sales-intelligence/hooks/useIsTextTruncated";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { ContactSourceBadge } from "app/pages/sales-intelligence/pages/ai-agent/components/ContactSourceBadge/ContactSourceBadge";
import { MAX_ATTENDEES } from "app/pages/sales-intelligence/pages/ai-agent/constants";

import { StyledContactCard } from "./styles";

type Props = {
    contact: Contact;
    index: number;
    isDisabled: boolean;
    isLimitDisabled: boolean;
    isHighlighted: boolean;
    onSelect: (contact: Contact) => void;
    onHighlightEnd: () => void;
};

export const ContactCard = ({
    contact,
    index,
    isDisabled,
    isLimitDisabled,
    isHighlighted,
    onSelect,
    onHighlightEnd,
}: Props) => {
    const [isCardTooltipOpen, setIsCardTooltipOpen] = useState(false);
    const [isSourceHovered, setIsSourceHovered] = useState(false);

    const name = contact.name ?? "";
    const title = contact.title ?? "";
    const isCardDisabled = isDisabled || isLimitDisabled;

    const { ref: nameRef, isTruncated: isNameTruncated } = useIsTextTruncated(name);
    const { ref: titleRef, isTruncated: isTitleTruncated } = useIsTextTruncated(title);
    const isTruncated = isNameTruncated || isTitleTruncated;

    // One tooltip for the whole card: anchoring it on the inner text lines made it overlap the card.
    const renderTooltipContent = (): ReactNode => {
        if (isLimitDisabled) {
            return translate("si.pages.ai_agent.form.keyAttendees.limit_reached", { max: MAX_ATTENDEES });
        }
        if (!isTruncated) {
            return null;
        }
        return (
            <Stack gap="2px">
                <Text
                    fontVariant="tooltip"
                    color={colorsPalettes.bluegrey[100]}
                >
                    {name}
                </Text>
                {title ? (
                    <Text
                        fontVariant="tooltip"
                        color={colorsPalettes.bluegrey[100]}
                    >
                        {title}
                    </Text>
                ) : null}
            </Stack>
        );
    };

    const tooltipContent = renderTooltipContent();

    const handleClick = () => {
        if (isDisabled) {
            return;
        }
        onSelect(contact);
    };

    const handleSourceMouseEnter = () => setIsSourceHovered(true);

    const handleSourceMouseLeave = () => setIsSourceHovered(false);

    const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
        // Nested elements (e.g. the checkbox) also fire animationend, which bubbles up to the card.
        if (event.target === event.currentTarget) {
            onHighlightEnd();
        }
    };

    return (
        <Tooltip
            enabled={Boolean(tooltipContent)}
            content={tooltipContent}
            placement="top"
            open={Boolean(isCardTooltipOpen && !isSourceHovered)}
            onOpenChange={setIsCardTooltipOpen}
        >
            <StyledContactCard
                onClick={handleClick}
                onAnimationEnd={handleAnimationEnd}
                isDisabled={isCardDisabled}
                isHighlighted={isHighlighted}
                data-automation={`ai-agent-attendees-card-${index}`}
            >
                <Checkbox
                    selected={contact.isSelected}
                    isDisabled={isCardDisabled}
                />
                <Box
                    maxWidth="100%"
                    overflow="hidden"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        gap="4px"
                    >
                        <StyledTextOverflow
                            ref={nameRef}
                            fontVariant="body2"
                            color={isCardDisabled ? colorsPalettes.midnight[100] : colorsPalettes.carbon[500]}
                        >
                            {name}
                        </StyledTextOverflow>
                        {contact.source ? (
                            <ContactSourceBadge
                                source={contact.source}
                                isDisabled={isCardDisabled}
                                isTooltipEnabled={!isLimitDisabled}
                                onMouseEnter={handleSourceMouseEnter}
                                onMouseLeave={handleSourceMouseLeave}
                            />
                        ) : null}
                    </Box>
                    <StyledTextOverflow
                        ref={titleRef}
                        fontVariant="caption"
                        color={isCardDisabled ? colorsPalettes.midnight[100] : colorsPalettes.carbon[300]}
                    >
                        {title}
                    </StyledTextOverflow>
                </Box>
            </StyledContactCard>
        </Tooltip>
    );
};
